import type { Row } from "@tanstack/react-table";
import { z } from "zod";

import type {
  PayrollDayData,
  TimeSheet,
} from "./timeTrackingPayroll.interfaces";
import {
  AcceptTimesheetSchema,
  AddTimeSheetSchema,
  BreaksSchema,
  CreateTimesheetPayrollSchema,
  HolidaySchema,
  HolidaySettingSchema,
  SaveEmployeeTimeSheetSchema,
  TimeShiftFormSchema,
  UpdateTimesheetPayrollSchema,
  UpdateTimeSheetSchema,
} from "./utils/timeTrackingSchemas.utils";

export enum ApprovalStatus {
  APPROVED = "APPROVED",
  APPROVAL_SENT = "APPROVAL_SENT",
  VOIDED = "VOIDED",
}

export enum TimeTrackingSessionKeys {
  FORTNIGHT = "fortnight",
}

export enum SheetType {
  EMPLOYEE = "employee",
  SUBCONTRACTOR = "subcontractor",
}

// TimeTracking form value types
export type AddTimeSheetFormValues = z.infer<typeof AddTimeSheetSchema>;
export type UpdateTimeSheetFormValues = z.infer<typeof UpdateTimeSheetSchema>;
export type CreateTimesheetPayrollFormValues = z.infer<
  typeof CreateTimesheetPayrollSchema
>;
export type UpdateTimesheetPayrollFormValues = z.infer<
  typeof UpdateTimesheetPayrollSchema
>;
export type SaveEmployeeTimeSheetFormValues = z.infer<
  typeof SaveEmployeeTimeSheetSchema
>;
export type AcceptTimesheetFormValues = z.infer<typeof AcceptTimesheetSchema>;

export type PayrollFormRow =
  | CreateTimesheetPayrollFormValues
  | UpdateTimesheetPayrollFormValues;

export type PayrollFormData = {
  payrollDetails: PayrollFormRow[];
  removePayrollIds?: string[];
};

export type PayrollEditorRowSource = "payroll" | "timesheet" | "empty";

export type PayrollEditorRow = PayrollDayData & {
  source?: PayrollEditorRowSource;
};

// TimeTrackingSettings form value types
export type BreaksFormValues = z.infer<typeof BreaksSchema>;
export type HolidayFormValues = z.infer<typeof HolidaySchema>;
export type HolidaySettingFormValues = z.infer<typeof HolidaySettingSchema>;
export type TimeShiftFormValues = z.infer<typeof TimeShiftFormSchema>;

export type TimeSheetCategory = "employee" | "subcontractor";
export type TimeTrackingSettingType = "break" | "holiday";

export type TimeSheetRow = Row<
  Omit<TimeSheet, "breakdown"> & {
    id?: string;
    breakdown: [
      {
        id: string;
        name: string;
        time: number;
      },
    ];
  }
>;

export type DayOfWeek =
  | "Sunday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday";

export type LabelValue = {
  label: string;
  value: string;
};
